import type {
  FieldProvenanceDto,
  MissionAssessmentEffectAiGenerationDataDto,
  MissionAssessmentEffectAiGenerationResponseDto,
  MissionAssessmentEffectAiQueryDto,
  MissionAssessmentEffectCreateDto,
} from '@@types/api.types';
import { LlmStreamStatus, MissionAssessmentChatObjectKind } from '@@types/api.types';
import { DropdownMenuComponent } from '@components/core/dropdown-menu.component';
import { IconButton } from '@components/core/icon-button.component';
import { EffectDefinitionTooltip } from '@components/shared/effect-definition.component';
import { FieldProvenanceBadge } from '@components/shared/field-provenance-badge.component';
import { useFormSuggestion } from '@components/shared/form-suggestion.component';
import { GenerateJSONButton } from '@components/shared/generate-json-button.component';
import { TextWithLabel } from '@components/shared/text-with-label.component';
import { Button } from '@components/ui/button';
import { withForm } from '@components/ui/form/form';
import { config } from '@config';
import { useGetDomains } from '@queries/domain.query';
import { useGetEffects } from '@queries/effect.query';
import { useGenerateMissionAssessmentEffect } from '@queries/mission-assessments.query';
import { useGetTargetSystems } from '@queries/target-system.query';
import { useGetUses } from '@queries/use.query';
import { missionAssessmentBaseSchema } from '@schemas/mission-assessment.schema';
import { sectionProvenance } from '@utils/mission-assessment-provenance.util';
import { EllipsisVerticalIcon, PlusIcon, SparklesIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { MISSION_ASSESSMENT_AI_ENDPOINTS } from '../../../../constants/mission-assessment-endpoints.constants';
import {
  MissionAssessmentEffectSuggestion,
  MissionAssessmentEffectSuggestionFields,
} from './mission-assessment-effect-suggestion.component';
import {
  getEffectSuggestionCatalogName,
  normalizeGeneratedMissionAssessmentEffect,
  parseGeneratedMissionAssessmentEffectData,
} from './mission-assessment-effect-suggestion.util';
import { MissionAssessmentFormEffectsModal } from './mission-assessment-form-effects-modal.component';
import { missionAssessmentDraftFormOptions } from './mission-assessment-form-options';

const EFFECT_SUGGESTION_KEY = 'effect';
const isAIEnabled = config.ai.enabled;

export const MissionAssessmentFormEffects = withForm({
  ...missionAssessmentDraftFormOptions,
  props: {
    provenance: undefined as Record<string, FieldProvenanceDto> | null | undefined,
    technologyId: undefined as string | undefined,
  },
  render: function Render({ form, provenance, technologyId }) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [effectToEdit, setEffectToEdit] = useState<{
      effect: MissionAssessmentEffectCreateDto;
      index: number;
    } | null>(null);

    const { data: availableUses, isLoading: isLoadingUses } = useGetUses();
    const { data: availableDomains, isLoading: isLoadingDomains } = useGetDomains();
    const { data: availableEffects, isLoading: isLoadingEffects } = useGetEffects();
    const { data: availableTargetSystems, isLoading: isLoadingTargetSystems } = useGetTargetSystems();
    const { mutateAsync: generateEffect } = useGenerateMissionAssessmentEffect();
    const {
      acceptSuggestion,
      rejectSuggestion,
      getSuggestion,
      getSuggestionModel,
      getIsGenerating,
      handleMetadata,
      handleGeneratingStart,
      handleGeneratingEnd,
      handleJsonData,
    } = useFormSuggestion<string, MissionAssessmentEffectAiGenerationDataDto>();

    const isLoading = isLoadingUses || isLoadingDomains || isLoadingEffects || isLoadingTargetSystems;
    const suggestion = getSuggestion(EFFECT_SUGGESTION_KEY);
    const effectSuggestion = typeof suggestion === 'object' ? suggestion : null;
    const isGeneratingEffect = getIsGenerating(EFFECT_SUGGESTION_KEY);

    const handleGenerationSuccess = useCallback(
      (result: MissionAssessmentEffectAiGenerationResponseDto) => {
        handleJsonData({ data: result.data, key: EFFECT_SUGGESTION_KEY });
        handleMetadata({
          key: EFFECT_SUGGESTION_KEY,
          metadata: {
            actualModel: result.metadata.actualModel,
            logId: result.metadata.logId,
            requestedModel: result.metadata.requestedModel,
            status: LlmStreamStatus.Completed,
          },
        });
      },
      [handleJsonData, handleMetadata],
    );

    const applyGeneratedEffect = useCallback(
      (data: MissionAssessmentEffectAiGenerationDataDto) => {
        const currentEffects = form.state.values.effects || [];
        const newItem = normalizeGeneratedMissionAssessmentEffect(data);

        form.setFieldValue('effects', [...currentEffects, newItem]);
      },
      [form],
    );

    const handleAcceptJsonSuggestion = useCallback(() => {
      acceptSuggestion(EFFECT_SUGGESTION_KEY, (data: string | MissionAssessmentEffectAiGenerationDataDto) => {
        if (typeof data !== 'object') return;

        applyGeneratedEffect(data);
      });
    }, [acceptSuggestion, applyGeneratedEffect]);

    const catalogs = useMemo(
      () => ({
        domains: availableDomains || [],
        effects: availableEffects || [],
        targetSystems: availableTargetSystems || [],
        uses: availableUses || [],
      }),
      [availableDomains, availableEffects, availableTargetSystems, availableUses],
    );

    const getDisplayName = (id: string, type: 'use' | 'domain' | 'effect') => {
      return getEffectSuggestionCatalogName({ catalogs, id, kind: type });
    };

    const getEffectDefinition = (id: string) => availableEffects?.find((effect) => effect.id === id)?.description;

    const handleAddEffect = () => {
      setEffectToEdit(null);
      setIsModalOpen(true);
    };

    const handleEditEffect = (effect: MissionAssessmentEffectCreateDto, index: number) => {
      setEffectToEdit({ effect, index });
      setIsModalOpen(true);
    };

    const closeModal = () => {
      setIsModalOpen(false);
      setEffectToEdit(null);
    };

    const handleEffectSubmit = (effectData: MissionAssessmentEffectCreateDto) => {
      if (effectToEdit !== null) {
        form.setFieldValue(`effects[${effectToEdit.index}]`, effectData);
      } else {
        form.setFieldValue('effects', (prev) => [...(prev || []), effectData]);
      }
      closeModal();
    };

    return (
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="flex items-center gap-2 text-lg text-gray-700">
            Effects
            <FieldProvenanceBadge
              context="form"
              provenance={sectionProvenance(provenance, 'EFFECTS')}
            />
          </h2>
        </div>

        <form.AppField
          name="effects"
          mode="array"
          validators={{
            onBlur: missionAssessmentBaseSchema.shape.effects,
          }}
        >
          {(field) => (
            <div>
              {field.state.value?.map((effect: MissionAssessmentEffectCreateDto, i: number) => (
                <div
                  key={`effect-${i}`}
                  className="bg-white border border-gray-200 rounded-lg p-4 mb-3 flex justify-between items-start w-full"
                >
                  <div className="grid grid-cols-3 w-full gap-4">
                    <TextWithLabel
                      label="Effect"
                      title={getDisplayName(effect.effectId, 'effect')}
                      badge={<EffectDefinitionTooltip definition={getEffectDefinition(effect.effectId)} />}
                      className="text-sm"
                    />
                    <TextWithLabel
                      label="Use"
                      title={getDisplayName(effect.useId, 'use')}
                      className="text-sm"
                    />
                    <TextWithLabel
                      label="Domain"
                      title={getDisplayName(effect.domainId, 'domain')}
                      className="text-sm"
                    />
                  </div>
                  <div className="ml-4">
                    <DropdownMenuComponent
                      trigger={
                        <div>
                          <IconButton
                            icon={<EllipsisVerticalIcon size={16} />}
                            variant="secondary"
                            size="md"
                            ariaLabel="Effect options"
                          />
                        </div>
                      }
                      items={[
                        {
                          text: 'Edit',
                          onClick: () => handleEditEffect(effect, i),
                        },
                        {
                          text: 'Delete',
                          onClick: () => field.removeValue(i),
                          variant: 'danger',
                        },
                      ]}
                    />
                  </div>
                </div>
              ))}

              {technologyId && isAIEnabled && !isGeneratingEffect && (
                <MissionAssessmentEffectSuggestion
                  className="mb-3"
                  data={effectSuggestion}
                  domains={availableDomains || []}
                  effects={availableEffects || []}
                  isGenerating={false}
                  model={getSuggestionModel(EFFECT_SUGGESTION_KEY)}
                  onAccept={handleAcceptJsonSuggestion}
                  onReject={() => rejectSuggestion(EFFECT_SUGGESTION_KEY)}
                  targetSystems={availableTargetSystems || []}
                  uses={availableUses || []}
                />
              )}

              <div className="mt-4 flex items-center gap-2">
                {!isGeneratingEffect && (
                  <Button
                    type="button"
                    onClick={handleAddEffect}
                    variant="link"
                    disabled={isLoading}
                  >
                    <PlusIcon className="size-4" />
                    Add Effect
                  </Button>
                )}

                {technologyId && isAIEnabled && (
                  <>
                    {!isGeneratingEffect && <div>or</div>}

                    <GenerateJSONButton<
                      MissionAssessmentEffectAiQueryDto,
                      MissionAssessmentEffectAiGenerationResponseDto
                    >
                      buttonProps={{
                        children: (
                          <>
                            <SparklesIcon className="size-4" />
                            Generate
                          </>
                        ),
                        variant: 'link',
                      }}
                      description={
                        <>
                          Use Tyr&apos;s AI assistant to generate a complete Mission Assessment Effect. All relevant
                          metadata and Effect catalog definitions will be used.
                        </>
                      }
                      generatingChildren={
                        <>
                          <SparklesIcon className="size-4" />
                          Generating...
                        </>
                      }
                      chatApiEndpoint={MISSION_ASSESSMENT_AI_ENDPOINTS.chat}
                      chatData={{ objectKind: MissionAssessmentChatObjectKind.EFFECT, technologyId }}
                      isGenerating={isGeneratingEffect}
                      mutateAsync={generateEffect}
                      onAcceptData={(value) => {
                        const parsed = parseGeneratedMissionAssessmentEffectData(value);
                        if (parsed) applyGeneratedEffect(parsed);
                      }}
                      onGeneratingEnd={() => handleGeneratingEnd(EFFECT_SUGGESTION_KEY)}
                      onGeneratingStart={() => handleGeneratingStart(EFFECT_SUGGESTION_KEY)}
                      onSuccess={handleGenerationSuccess}
                      params={{
                        technologyId,
                      }}
                      renderDataArtifact={(value) => {
                        const parsed = parseGeneratedMissionAssessmentEffectData(value);
                        if (!parsed) return null;

                        return (
                          <MissionAssessmentEffectSuggestionFields
                            data={parsed}
                            domains={availableDomains || []}
                            effects={availableEffects || []}
                            targetSystems={availableTargetSystems || []}
                            uses={availableUses || []}
                          />
                        );
                      }}
                      title="Mission Assessment Effect"
                    />
                  </>
                )}
              </div>

              {isModalOpen && (
                <MissionAssessmentFormEffectsModal
                  isOpen={isModalOpen}
                  onClose={closeModal}
                  onSubmit={(effectData) => handleEffectSubmit(effectData)}
                  effectToEdit={effectToEdit?.effect || null}
                  technologyId={technologyId}
                />
              )}
            </div>
          )}
        </form.AppField>
      </div>
    );
  },
});
